import * as React from 'react';
import { useMemo } from 'react';

import { useSubfolderLanguageAndLocale } from '@coursera-web/internationalization-lib';
import type {
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
  DescriptionPage_MarketingSource as MarketingSource,
} from '__generated__/graphql-types';

import type PAGE_TYPES from 'bundles/unified-description-page-common/constants/pageTypes';
import PageInfoContext from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { getSpecializationProductUrl } from 'bundles/unified-description-page-common/utils';
import getCourseLink from 'bundles/unified-description-page-common/utils/getCourseLink';

type Props = {
  children: React.ReactNode;
  programSlug?: string;
  productSlug?: string;
  marketingSource?: MarketingSource;
  pageType?: (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
  navigationIdMap?: Record<string, string>;
  hasDubbedLanguages?: boolean;
};

const PageInfoProvider = ({
  programSlug,
  children,
  productSlug,
  marketingSource,
  pageType,
  navigationIdMap,
  hasDubbedLanguages,
}: Props) => {
  const subfolderLocaleCode = useSubfolderLanguageAndLocale();
  const value = useMemo(
    () => ({
      getCourseLink: ({ courseSlug, specializationSlug }: { courseSlug: string; specializationSlug?: string }) =>
        getCourseLink({
          programSlug,
          courseSlug,
          specializationSlug,
          marketingSource,
          localizedLanguageRegionCode: subfolderLocaleCode,
        }),
      getSpecializationLink: ({
        productSlug: s12nProductSlug,
        productVariant,
      }: {
        productSlug: string;
        productVariant: DescriptionPageProductVariant;
      }) =>
        getSpecializationProductUrl({
          programSlug,
          productSlug: s12nProductSlug,
          productVariant,
          marketingSource,
          localizedLanguageRegionCode: subfolderLocaleCode,
        }),
      productSlug,
      pageType,
      navigationIdMap,
      marketingSource,
      subfolderLocaleCode,
      hasDubbedLanguages,
    }),
    [programSlug, productSlug, marketingSource, pageType, navigationIdMap, subfolderLocaleCode, hasDubbedLanguages]
  );
  return <PageInfoContext.Provider value={value}>{children}</PageInfoContext.Provider>;
};

export default PageInfoProvider;
